document.documentElement.classList.remove("no-js");

window.dataLayer = window.dataLayer || [];

const createThemeEventId = () => {
  if (typeof window.crypto?.randomUUID === "function") return window.crypto.randomUUID();
  return `vivo-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const pushThemeEvent = (eventName, detail = {}) => {
  const payload = {
    event: eventName,
    event_id: createThemeEventId(),
    event_time: new Date().toISOString(),
    page_path: window.location.pathname,
    currency: window.Shopify?.currency?.active || "USD",
    ...detail
  };

  window.dataLayer.push(payload);
  document.documentElement.dataset.lastThemeEvent = eventName;

  if (typeof window.Shopify?.analytics?.publish === "function") {
    document.documentElement.dataset.themeAnalyticsStatus = "pending";
    Promise.resolve(window.Shopify.analytics.publish(`vivo_nostra:${eventName}`, payload))
      .then((published) => {
        document.documentElement.dataset.themeAnalyticsStatus = published ? "published" : "not-published";
      })
      .catch(() => {
        document.documentElement.dataset.themeAnalyticsStatus = "failed";
      });
  } else {
    document.documentElement.dataset.themeAnalyticsStatus = "unavailable";
  }

  document.dispatchEvent(new CustomEvent("vivo:analytics", { detail: payload }));

  if (new URLSearchParams(window.location.search).get("vivo_analytics_debug") === "1") {
    window.__vivoAnalyticsDebug = window.__vivoAnalyticsDebug || [];
    window.__vivoAnalyticsDebug.push(payload);
    window.__vivoAnalyticsDebug = window.__vivoAnalyticsDebug.slice(-50);
  }
};

const pushKlaviyoEvent = (eventName, detail = {}) => {
  if (typeof window.klaviyo?.track === "function") {
    window.klaviyo.track(eventName, detail);
    return;
  }

  window._learnq = window._learnq || [];
  window._learnq.push(["track", eventName, detail]);
};

const identifyKlaviyoProfile = (profile = {}) => {
  if (!profile.email) return;
  window._learnq = window._learnq || [];
  window._learnq.push(["identify", profile]);
};

const menuToggle = document.querySelector("[data-menu-toggle]");
const mobileMenu = document.querySelector("[data-mobile-menu]");

const closeMobileMenu = () => {
  if (!menuToggle || !mobileMenu) return;
  mobileMenu.classList.remove("is-open");
  menuToggle.setAttribute("aria-expanded", "false");
  const label = menuToggle.querySelector(".visually-hidden");
  if (label) label.textContent = "Open menu";
};

if (menuToggle && mobileMenu) {
  menuToggle.addEventListener("click", () => {
    const open = mobileMenu.classList.toggle("is-open");
    menuToggle.setAttribute("aria-expanded", String(open));
    const label = menuToggle.querySelector(".visually-hidden");
    if (label) label.textContent = open ? "Close menu" : "Open menu";
  });
  mobileMenu.addEventListener("click", (event) => {
    if (event.target.closest("a")) closeMobileMenu();
  });
}

document.addEventListener("click", (event) => {
  document.querySelectorAll(".nav-dropdown[open]").forEach((dropdown) => {
    if (!dropdown.contains(event.target)) dropdown.removeAttribute("open");
  });
});

const cartDrawer = document.querySelector("[data-cart-drawer]");
const cartPanel = document.querySelector("[data-cart-panel]");
const cartItems = document.querySelector("[data-cart-items]");
const cartEmpty = document.querySelector("[data-cart-empty]");
const cartSummary = document.querySelector("[data-cart-summary]");
const cartLoading = document.querySelector("[data-cart-loading]");
const cartStatus = document.querySelector("[data-cart-status]");
const cartSubtotal = document.querySelector("[data-cart-subtotal]");
const cartShippingCopy = document.querySelector("[data-cart-shipping-copy]");
const cartShippingProgress = document.querySelector("[data-cart-shipping-progress]");
const cartRoot = window.Shopify?.routes?.root || "/";
const freeShippingThreshold = 4900;
let cartDrawerLastFocus = null;
let cartRequestPending = false;

const cartRoute = (path) => `${cartRoot}${path.replace(/^\//, "")}`;

const formatCartMoney = (cents) => {
  const currency = window.Shopify?.currency?.active || "USD";
  try {
    return new Intl.NumberFormat(document.documentElement.lang || "en", {
      style: "currency",
      currency
    }).format(Number(cents || 0) / 100);
  } catch {
    return `$${(Number(cents || 0) / 100).toFixed(2)}`;
  }
};

const cleanCartVariantTitle = (title) => title.replace(/\s+-\s+(20|25|30)%\s+Off/gi, "").trim();
const cleanCartProductTitle = (title) => title.replace(/Vivonostra™?/gi, "Vivo Nostra").trim();

const setCartStatus = (message) => {
  if (cartStatus) cartStatus.textContent = message;
};

const setCartBusy = (busy) => {
  cartRequestPending = busy;
  cartPanel?.setAttribute("aria-busy", String(busy));
  cartDrawer?.querySelectorAll("[data-cart-quantity], [data-cart-remove]").forEach((control) => {
    control.disabled = busy;
  });
};

const updateCartCount = (count) => {
  document.querySelectorAll("[data-cart-count]").forEach((element) => {
    element.textContent = `(${count})`;
    element.hidden = count === 0;
  });
  document.querySelectorAll("[data-cart-count-badge]").forEach((element) => {
    element.textContent = String(count);
    element.hidden = count === 0;
  });
};

const buildCartItem = (item) => {
  const productTitle = cleanCartProductTitle(item.product_title);
  const article = document.createElement("article");
  article.className = "native-cart-item";
  article.setAttribute("role", "listitem");

  const imageLink = document.createElement("a");
  imageLink.href = item.url;
  imageLink.setAttribute("aria-label", productTitle);
  const imageUrl = item.handle === "starter-routine"
    ? cartDrawer?.dataset.starterImage
    : item.featured_image?.url || item.image;
  if (imageUrl) {
    const image = document.createElement("img");
    image.className = "native-cart-item-image";
    image.src = imageUrl;
    image.alt = productTitle;
    image.width = 176;
    image.height = 176;
    image.loading = "lazy";
    imageLink.append(image);
  }

  const copy = document.createElement("div");
  copy.className = "native-cart-item-copy";
  const title = document.createElement("a");
  title.className = "native-cart-item-title";
  title.href = item.url;
  title.textContent = productTitle;
  copy.append(title);

  if (item.variant_title && item.variant_title !== "Default Title") {
    const meta = document.createElement("p");
    meta.className = "native-cart-item-meta";
    meta.textContent = cleanCartVariantTitle(item.variant_title);
    copy.append(meta);
  }
  if (item.selling_plan_allocation?.selling_plan?.name) {
    const plan = document.createElement("p");
    plan.className = "native-cart-item-meta";
    plan.textContent = item.selling_plan_allocation.selling_plan.name;
    copy.append(plan);
  }

  const quantity = document.createElement("div");
  quantity.className = "native-cart-quantity";
  const decrease = document.createElement("button");
  decrease.type = "button";
  decrease.dataset.cartQuantity = "decrease";
  decrease.dataset.cartKey = item.key;
  decrease.dataset.cartNextQuantity = String(Math.max(0, item.quantity - 1));
  decrease.setAttribute("aria-label", `Decrease ${productTitle} quantity`);
  decrease.textContent = "-";
  const value = document.createElement("span");
  value.textContent = String(item.quantity);
  value.setAttribute("aria-label", `Quantity ${item.quantity}`);
  const increase = document.createElement("button");
  increase.type = "button";
  increase.dataset.cartQuantity = "increase";
  increase.dataset.cartKey = item.key;
  increase.dataset.cartNextQuantity = String(item.quantity + 1);
  increase.setAttribute("aria-label", `Increase ${productTitle} quantity`);
  increase.textContent = "+";
  quantity.append(decrease, value, increase);
  copy.append(quantity);

  const remove = document.createElement("button");
  remove.className = "native-cart-remove";
  remove.type = "button";
  remove.dataset.cartRemove = "";
  remove.dataset.cartKey = item.key;
  remove.setAttribute("aria-label", `Remove ${productTitle} from cart`);
  remove.textContent = "Remove";
  copy.append(remove);

  const price = document.createElement("div");
  price.className = "native-cart-item-price";
  if (item.original_line_price > item.final_line_price) {
    const original = document.createElement("s");
    original.textContent = formatCartMoney(item.original_line_price);
    price.append(original);
  }
  const finalPrice = document.createElement("strong");
  finalPrice.textContent = formatCartMoney(item.final_line_price);
  price.append(finalPrice);

  article.append(imageLink, copy, price);
  return article;
};

const renderCart = (cart) => {
  if (!cartItems || !cartEmpty || !cartSummary) return;
  const hasItems = cart.item_count > 0;
  cartItems.replaceChildren(...cart.items.map(buildCartItem));
  cartItems.hidden = !hasItems;
  cartEmpty.hidden = hasItems;
  cartSummary.hidden = !hasItems;
  updateCartCount(cart.item_count);

  if (!hasItems) {
    setCartStatus("Your cart is empty.");
    return;
  }

  if (cartSubtotal) cartSubtotal.textContent = formatCartMoney(cart.total_price);
  const remaining = Math.max(0, freeShippingThreshold - cart.total_price);
  const progress = Math.min(100, Math.round((cart.total_price / freeShippingThreshold) * 100));
  if (cartShippingCopy) {
    cartShippingCopy.textContent = remaining > 0
      ? `Add ${formatCartMoney(remaining)} for free US shipping.`
      : "Free US shipping unlocked.";
  }
  if (cartShippingProgress) {
    cartShippingProgress.setAttribute("aria-valuenow", String(progress));
    const bar = cartShippingProgress.querySelector("span");
    if (bar) bar.style.width = `${progress}%`;
  }
  setCartStatus(`${cart.item_count} ${cart.item_count === 1 ? "item" : "items"} in cart.`);
};

const fetchCart = async () => {
  const response = await fetch(cartRoute("cart.js"), {
    headers: { Accept: "application/json" },
    credentials: "same-origin"
  });
  if (!response.ok) throw new Error("Cart could not be loaded.");
  return response.json();
};

const closeCartDrawer = ({ restoreFocus = true } = {}) => {
  if (!cartDrawer || cartDrawer.hidden) return;
  cartDrawer.hidden = true;
  document.documentElement.classList.remove("cart-drawer-open");
  if (restoreFocus) cartDrawerLastFocus?.focus?.({ preventScroll: true });
  pushThemeEvent("cart_drawer_close");
};

const showCartError = (message) => {
  if (!cartDrawer || !cartLoading) return;
  cartLoading.hidden = false;
  cartLoading.textContent = message;
  setCartStatus(message);
};

const openCartDrawer = async (source = "manual", prefetchedCart = null) => {
  if (!cartDrawer || !cartPanel) {
    window.location.assign(cartRoute("cart"));
    return;
  }
  cartDrawerLastFocus = document.activeElement;
  cartDrawer.hidden = false;
  document.documentElement.classList.add("cart-drawer-open");
  closeMobileMenu();
  cartPanel.focus({ preventScroll: true });
  pushThemeEvent("cart_drawer_open", { source });

  if (cartLoading) {
    cartLoading.textContent = "Updating cart...";
    cartLoading.hidden = Boolean(prefetchedCart);
  }
  try {
    const cart = prefetchedCart || await fetchCart();
    renderCart(cart);
    if (cartLoading) cartLoading.hidden = true;
  } catch (error) {
    showCartError(error.message || "Cart could not be loaded.");
  }
};

const changeCartLine = async (key, quantity) => {
  if (cartRequestPending) return;
  setCartBusy(true);
  if (cartLoading) {
    cartLoading.textContent = "Updating cart...";
    cartLoading.hidden = false;
  }
  try {
    const response = await fetch(cartRoute("cart/change.js"), {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json"
      },
      credentials: "same-origin",
      body: JSON.stringify({ id: key, quantity })
    });
    const cart = await response.json();
    if (!response.ok) throw new Error(cart.description || "Cart could not be updated.");
    renderCart(cart);
    if (cartLoading) cartLoading.hidden = true;
    pushThemeEvent("cart_drawer_quantity_change", { quantity });
  } catch (error) {
    showCartError(error.message || "Cart could not be updated.");
  } finally {
    setCartBusy(false);
  }
};

document.addEventListener("click", (event) => {
  const close = event.target.closest("[data-cart-close]");
  if (close) {
    closeCartDrawer();
    return;
  }

  const trigger = event.target.closest("[data-cart-trigger]");
  if (trigger && window.location.pathname !== cartRoute("cart")) {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    openCartDrawer("header");
    return;
  }

  const quantity = event.target.closest("[data-cart-quantity]");
  if (quantity) {
    changeCartLine(quantity.dataset.cartKey, Number(quantity.dataset.cartNextQuantity));
    return;
  }

  const remove = event.target.closest("[data-cart-remove]");
  if (remove) changeCartLine(remove.dataset.cartKey, 0);
});

const guidePopup = document.querySelector("[data-guide-popup]");
const guidePopupOpeners = document.querySelectorAll("[data-guide-popup-open]");
const guidePopupClosers = document.querySelectorAll("[data-guide-popup-close]");
const guidePopupSuccess = document.querySelector("[data-guide-popup-success]");
let guidePopupLastFocus = null;

const openGuidePopup = (source = "manual") => {
  if (!guidePopup) return;
  closeCartDrawer({ restoreFocus: false });
  guidePopupLastFocus = document.activeElement;
  guidePopup.hidden = false;
  document.documentElement.classList.add("guide-popup-open");
  guidePopup.querySelector("input[type='email'], [data-guide-popup-close]")?.focus({ preventScroll: true });
  pushThemeEvent("guide_popup_open", { source });
};

const closeGuidePopup = () => {
  if (!guidePopup || guidePopup.hidden) return;
  guidePopup.hidden = true;
  document.documentElement.classList.remove("guide-popup-open");
  guidePopupLastFocus?.focus?.({ preventScroll: true });
  pushThemeEvent("guide_popup_close");
};

guidePopupOpeners.forEach((button) => {
  button.addEventListener("click", () => {
    const formId = button.dataset.klaviyoFormId;
    const isThemePreview = Boolean(
      window.Shopify?.designMode || new URLSearchParams(window.location.search).has("preview_theme_id")
    );

    if (formId && !isThemePreview) {
      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push(["openForm", formId]);
      pushThemeEvent("guide_form_open", { provider: "klaviyo", form_id: formId });
      return;
    }

    openGuidePopup("button");
  });
});

guidePopupClosers.forEach((button) => {
  button.addEventListener("click", closeGuidePopup);
});

if (guidePopupSuccess || new URLSearchParams(window.location.search).has("customer_posted")) {
  window.setTimeout(() => openGuidePopup("success"), 250);
}

document.querySelectorAll("[data-routine-finder]").forEach((finder) => {
  const result = finder.querySelector("[data-routine-result]");
  const options = finder.querySelectorAll(".finder-option");
  if (!result || !options.length) return;

  const image = result.querySelector("img");
  const badge = result.querySelector(".badge");
  const title = result.querySelector("h3");
  const copy = result.querySelector(".finder-copy > p:not(.finder-note)");
  const note = result.querySelector(".finder-note");
  const action = result.querySelector("a.btn");

  options.forEach((option) => {
    option.addEventListener("click", () => {
      options.forEach((item) => item.classList.toggle("is-selected", item === option));
      if (badge) badge.textContent = option.dataset.badge || "Recommended";
      const isStarter = option.dataset.goal === "complete_routine";
      if (badge) badge.classList.toggle("badge-accent", isStarter);
      result.classList.toggle("is-starter-result", isStarter);
      if (title) title.textContent = option.dataset.title || title.textContent;
      if (copy) copy.textContent = option.dataset.copy || copy.textContent;
      if (note) note.textContent = option.dataset.note || "";
      if (action) {
        action.href = option.dataset.url || action.href;
        action.textContent = option.dataset.action || "Shop product";
      }
      if (image && option.dataset.image) {
        image.removeAttribute("srcset");
        image.removeAttribute("sizes");
        image.src = option.dataset.image;
        image.alt = option.dataset.title || image.alt;
      }
      pushThemeEvent("routine_finder_select", {
        goal: option.dataset.goal || "",
        product_title: option.dataset.title || "",
        product_url: option.dataset.url || ""
      });
      pushKlaviyoEvent("Routine Finder Selected", {
        Goal: option.dataset.goal || "",
        RecommendedProduct: option.dataset.title || "",
        ProductURL: option.dataset.url || "",
        PageURL: window.location.href
      });
    });
  });
});

document.querySelectorAll("[data-product-gallery]").forEach((gallery) => {
  const mainImage = gallery.querySelector("[data-product-main-image]");
  const thumbs = gallery.querySelectorAll("[data-gallery-thumb]");
  if (!mainImage || !thumbs.length) return;

  thumbs.forEach((thumb) => {
    thumb.addEventListener("click", () => {
      const nextImage = thumb.dataset.galleryImage;
      if (!nextImage) return;
      thumbs.forEach((item) => item.classList.toggle("is-selected", item === thumb));
      mainImage.removeAttribute("srcset");
      mainImage.removeAttribute("sizes");
      mainImage.src = nextImage;
      mainImage.alt = thumb.dataset.galleryAlt || mainImage.alt;
      pushThemeEvent("product_gallery_select", { image_url: nextImage });
    });
  });
});

document.querySelectorAll("[data-product-page]").forEach((page) => {
  const variantControl = page.querySelector("[data-product-variant-select]");
  const initialOption = variantControl?.tagName === "SELECT" ? variantControl.selectedOptions[0] : null;
  pushThemeEvent("vivo_product_view", {
    product_id: page.dataset.productId || "",
    product_title: page.dataset.productTitle || "",
    product_handle: page.dataset.productHandle || "",
    product_url: page.dataset.productUrl || "",
    variant_id: variantControl?.value || "",
    variant_title: initialOption?.dataset.variantTitle || "",
    sku: initialOption?.dataset.sku || "",
    price_cents: Number(initialOption?.dataset.priceCents || 0)
  });

  const price = page.querySelector("[data-product-price]");
  const comparePrice = page.querySelector("[data-product-compare-price]");
  const savings = page.querySelector("[data-product-savings]");
  const submit = page.querySelector("[data-product-submit]");
  const productForm = page.querySelector(".product-form");
  const stickyVariant = document.querySelector("[data-sticky-variant-id]");
  const stickyPrice = document.querySelector("[data-sticky-product-price]");
  const stickySubmit = document.querySelector("[data-sticky-product-submit]");
  const stickySellingPlan = document.querySelector("[data-sticky-selling-plan]");
  const pomadeSizeControl = page.querySelector("[data-pomade-size-select]");
  const pomadeUnitsControl = page.querySelector("[data-pomade-units-select]");
  const isPomade = page.dataset.productHandle === "hair-styling-products-for-men";

  const syncVariantGallery = (units) => {
    const gallery = page.querySelector("[data-product-gallery]");
    if (!gallery || !units) return;
    gallery.querySelectorAll("[data-gallery-thumb]").forEach((thumb) => {
      const thumbUnits = Number(thumb.dataset.galleryUnits || 0);
      if (thumbUnits) {
        const isCurrent = thumbUnits === units;
        thumb.hidden = !isCurrent;
        thumb.classList.toggle("is-selected", isCurrent);
      }
    });
  };

  const normalizeVariantOptions = () => {
    if (!variantControl || variantControl.tagName !== "SELECT") return;
    [...variantControl.options].forEach((option) => {
      if (option.dataset.available === "true") {
        option.removeAttribute("hidden");
        option.hidden = false;
      }
      if (!option.textContent.trim()) {
        const units = Number(option.dataset.units || 1);
        const label = `${units} ${units === 1 ? "Unit" : "Units"}`;
        option.textContent = `${label} - ${option.dataset.price || ""}`.trim();
      }
    });
  };

  const syncPomadeControlsFromVariant = (option) => {
    if (!isPomade || !option) return;
    if (pomadeSizeControl && option.dataset.size) pomadeSizeControl.value = option.dataset.size;
    if (pomadeUnitsControl && option.dataset.units) pomadeUnitsControl.value = option.dataset.units;
  };

  const syncPomadeUnitAvailability = () => {
    if (!isPomade || !variantControl || !pomadeSizeControl || !pomadeUnitsControl) return;
    const size = pomadeSizeControl.value;
    let firstAvailable = "";
    [...pomadeUnitsControl.options].forEach((unitOption) => {
      const matchingVariant = [...variantControl.options].find((option) =>
        option.dataset.size === size && option.dataset.units === unitOption.value
      );
      const available = Boolean(matchingVariant && matchingVariant.dataset.available === "true");
      unitOption.disabled = !available;
      if (available && !firstAvailable) firstAvailable = unitOption.value;
    });
    if (pomadeUnitsControl.selectedOptions[0]?.disabled && firstAvailable) {
      pomadeUnitsControl.value = firstAvailable;
    }
  };

  const selectPomadeVariant = (trackSelection = true) => {
    if (!isPomade || !variantControl || !pomadeSizeControl || !pomadeUnitsControl) return;
    syncPomadeUnitAvailability();
    const size = pomadeSizeControl.value;
    const units = pomadeUnitsControl.value;
    const matchingVariant = [...variantControl.options].find((option) =>
      option.dataset.size === size && option.dataset.units === units && option.dataset.available === "true"
    );
    if (!matchingVariant) return;
    variantControl.value = matchingVariant.value;
    updateVariant(trackSelection);
  };

  if (variantControl?.tagName === "SELECT" && window.MutationObserver) {
    new MutationObserver(normalizeVariantOptions).observe(variantControl, {
      subtree: true,
      attributes: true,
      attributeFilter: ["hidden"]
    });
  }

  const syncSellingPlan = () => {
    if (!productForm || !stickySellingPlan) return;
    const selectedPlan = productForm.querySelector("input[name='selling_plan']:checked, select[name='selling_plan'], input[name='selling_plan'][type='hidden']");
    const value = selectedPlan?.value || "";
    stickySellingPlan.value = value;
    if (value) stickySellingPlan.setAttribute("name", "selling_plan");
    else stickySellingPlan.removeAttribute("name");
  };

  const updateVariant = (trackSelection = false) => {
    if (!variantControl) return;
    normalizeVariantOptions();
    const option = variantControl.tagName === "SELECT" ? variantControl.selectedOptions[0] : null;
    const variantId = variantControl.value;
    const available = option ? option.dataset.available === "true" : !submit?.disabled;
    const nextPrice = option?.dataset.price;
    const nextComparePrice = option?.dataset.comparePrice || "";
    const nextBundleSavings = option?.dataset.bundleSavingsValue || "";
    const nextPriceCents = Number(option?.dataset.priceCents || 0);
    const nextComparePriceCents = Number(option?.dataset.comparePriceCents || 0);
    const savingsPercent = nextComparePriceCents > nextPriceCents
      ? Math.round(((nextComparePriceCents - nextPriceCents) / nextComparePriceCents) * 100)
      : 0;
    const variantImage = option?.dataset.image || "";
    const variantImageAlt = option?.dataset.imageAlt || "";
    const variantUnits = Number(option?.dataset.units || 1);
    const mainImage = page.querySelector("[data-product-main-image]");

    syncPomadeControlsFromVariant(option);
    syncPomadeUnitAvailability();

    if (price && nextPrice) price.textContent = nextPrice;
    if (comparePrice) {
      comparePrice.textContent = nextComparePrice;
      comparePrice.hidden = !nextComparePrice;
    }
    if (savings) {
      savings.textContent = savingsPercent > 0 ? `Save ${savingsPercent}%` : "";
      savings.hidden = savingsPercent <= 0;
    }
    const bundleSavings = page.querySelector("[data-bundle-savings-display]");
    if (bundleSavings) {
      bundleSavings.innerHTML = nextBundleSavings
        ? `Bundle price <span aria-hidden="true">·</span> Save ${nextBundleSavings} vs. buying separately`
        : "";
      bundleSavings.hidden = !nextBundleSavings;
    }
    if (submit) {
      submit.disabled = !available;
      submit.textContent = available ? "Add to cart" : "Sold out";
      submit.setAttribute("aria-label", available ? "Add to cart" : "Sold out");
    }
    if (stickyVariant) stickyVariant.value = variantId;
    if (stickyPrice && nextPrice) stickyPrice.textContent = nextPrice;
    if (stickySubmit) {
      stickySubmit.disabled = !available;
      stickySubmit.textContent = available ? "Add to cart" : "Sold out";
      stickySubmit.setAttribute("aria-label", available ? "Add to cart" : "Sold out");
    }
    if (mainImage && variantImage) {
      mainImage.removeAttribute("srcset");
      mainImage.removeAttribute("sizes");
      mainImage.src = variantImage;
      if (variantImageAlt) mainImage.alt = variantImageAlt;
    }
    syncVariantGallery(variantUnits);
    if (variantControl.tagName === "SELECT" && variantId) {
      const url = new URL(window.location.href);
      url.searchParams.set("variant", variantId);
      window.history.replaceState({}, "", url);
      if (trackSelection) {
        pushThemeEvent("product_variant_select", {
          product_id: page.dataset.productId || "",
          product_handle: page.dataset.productHandle || "",
          product_title: page.dataset.productTitle || "",
          variant_id: variantId,
          variant_title: option?.dataset.variantTitle || "",
          sku: option?.dataset.sku || "",
          units: variantUnits,
          price: nextPrice || "",
          price_cents: nextPriceCents
        });
      }
    }
    syncSellingPlan();
  };

  variantControl?.addEventListener("change", () => updateVariant(true));
  pomadeSizeControl?.addEventListener("change", () => selectPomadeVariant(true));
  pomadeUnitsControl?.addEventListener("change", () => selectPomadeVariant(true));
  productForm?.addEventListener("change", (event) => {
    if (event.target.matches("[name='selling_plan'], [data-selling-plan]")) syncSellingPlan();
  });
  if (productForm && stickySellingPlan) {
    new MutationObserver(syncSellingPlan).observe(productForm, { childList: true, subtree: true });
  }
  normalizeVariantOptions();
  updateVariant();
});

document.addEventListener("keydown", (event) => {
  if (event.key === "Escape") {
    closeMobileMenu();
    closeGuidePopup();
    closeCartDrawer();
    document.querySelectorAll(".nav-dropdown[open]").forEach((dropdown) => dropdown.removeAttribute("open"));
  }
  if (event.key !== "Tab") return;
  const focusRoot = cartDrawer && !cartDrawer.hidden ? cartPanel : guidePopup && !guidePopup.hidden ? guidePopup : null;
  if (!focusRoot) return;
  const focusable = Array.from(focusRoot.querySelectorAll("a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])"));
  if (!focusable.length) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
});

const enhanceTrack123Inputs = () => {
  if (!window.location.pathname.startsWith("/apps/track123")) return true;

  const orderAndTrackingInputs = document.querySelectorAll(".track123_form_input1");
  const emailInput = document.querySelector(".track123_form_input2");
  orderAndTrackingInputs[0]?.setAttribute("aria-label", "Order number");
  emailInput?.setAttribute("aria-label", "Email or phone");
  orderAndTrackingInputs[1]?.setAttribute("aria-label", "Tracking number");

  return orderAndTrackingInputs.length >= 2 && Boolean(emailInput);
};

if (!enhanceTrack123Inputs() && window.location.pathname.startsWith("/apps/track123")) {
  const track123Observer = new MutationObserver(() => {
    if (enhanceTrack123Inputs()) track123Observer.disconnect();
  });
  track123Observer.observe(document.querySelector("#MainContent") || document.body, { childList: true, subtree: true });
  window.setTimeout(() => track123Observer.disconnect(), 10000);
}

const businessLeadStorageKey = "vivo_business_lead_pending";

const setBusinessFieldValue = (form, selector, value) => {
  const field = form?.querySelector(selector);
  if (field) field.value = value || "";
};

const getBusinessReferrer = () => {
  if (!document.referrer) return { source: "Direct", referrer: "" };
  try {
    const referrer = new URL(document.referrer);
    return {
      source: referrer.hostname === window.location.hostname ? "On-site navigation" : referrer.hostname,
      referrer: referrer.hostname
    };
  } catch {
    return { source: "Direct", referrer: "" };
  }
};

const buildBusinessLeadContext = (form, markSubmitted = false) => {
  const params = new URLSearchParams(window.location.search);
  const referrer = getBusinessReferrer();
  const leadIdField = form.querySelector("[data-business-lead-id]");
  const leadId = leadIdField?.value || `VN-${new Date().toISOString().slice(0, 10).replaceAll("-", "")}-${createThemeEventId().slice(-8).toUpperCase()}`;
  const inquiryType = form.querySelector("[data-business-inquiry-select]")?.value || "";
  const timeline = form.querySelector("[data-business-timeline]")?.value || "";
  const openingOrderField = form.querySelector("[name='contact[Expected opening order]']");
  const audienceReachField = form.querySelector("[name='contact[Audience or event reach]']");
  const openingOrder = openingOrderField && !openingOrderField.disabled ? openingOrderField.value : "";
  const audienceReach = audienceReachField && !audienceReachField.disabled ? audienceReachField.value : "";
  const utmSource = params.get("utm_source") || "";
  const utmMedium = params.get("utm_medium") || "";
  const utmCampaign = params.get("utm_campaign") || "";
  const utmContent = params.get("utm_content") || "";
  const source = utmSource || referrer.source;
  const prioritySignals = ["$500-$999", "$1,000+", "25,000-99,999", "100,000+"];
  const hasPriorityValue = prioritySignals.includes(openingOrder) || prioritySignals.includes(audienceReach);
  const priority = hasPriorityValue && timeline !== "Exploring for later" ? "Priority" : "Standard";
  const submittedAt = markSubmitted ? new Date().toISOString() : form.querySelector("[data-business-submitted-at]")?.value || "";
  const fullName = form.querySelector("[name='contact[name]']")?.value.trim() || "";

  setBusinessFieldValue(form, "[data-business-lead-id]", leadId);
  setBusinessFieldValue(form, "[data-business-lead-source]", source);
  setBusinessFieldValue(form, "[data-business-lead-priority]", priority);
  setBusinessFieldValue(form, "[data-business-utm-source]", utmSource);
  setBusinessFieldValue(form, "[data-business-utm-medium]", utmMedium);
  setBusinessFieldValue(form, "[data-business-utm-campaign]", utmCampaign);
  setBusinessFieldValue(form, "[data-business-utm-content]", utmContent);
  setBusinessFieldValue(form, "[data-business-landing-page]", window.location.pathname);
  setBusinessFieldValue(form, "[data-business-referrer]", referrer.referrer);
  setBusinessFieldValue(form, "[data-business-submitted-at]", submittedAt);

  return {
    profile: {
      email: form.querySelector("[name='contact[email]']")?.value.trim() || "",
      first_name: fullName.split(/\s+/)[0] || "",
      business_contact_name: fullName,
      organization: form.querySelector("[name='contact[Business or channel name]']")?.value.trim() || "",
      business_location: form.querySelector("[name='contact[Location]']")?.value.trim() || "",
      business_website: form.querySelector("[name='contact[Website or social profile]']")?.value.trim() || "",
      business_inquiry_type: inquiryType,
      business_lead_id: leadId,
      business_lead_stage: "New",
      business_lead_priority: priority,
      business_lead_source: source,
      business_timeline: timeline,
      business_opening_order: openingOrder,
      business_audience_reach: audienceReach,
      business_utm_source: utmSource,
      business_utm_medium: utmMedium,
      business_utm_campaign: utmCampaign,
      business_created_at: submittedAt
    },
    event: {
      lead_id: leadId,
      inquiry_type: inquiryType,
      opening_order: openingOrder,
      audience_or_event_reach: audienceReach,
      timeline,
      priority,
      source,
      utm_source: utmSource,
      utm_medium: utmMedium,
      utm_campaign: utmCampaign,
      utm_content: utmContent,
      landing_page: window.location.pathname,
      submitted_at: submittedAt
    },
    analytics: {
      lead_id: leadId,
      inquiry_type: inquiryType,
      timeline,
      priority,
      source,
      utm_campaign: utmCampaign
    }
  };
};

const businessForm = document.querySelector(".business-form");
if (businessForm) buildBusinessLeadContext(businessForm);

const businessSuccess = document.querySelector("[data-business-form-success]");
if (businessSuccess) {
  try {
    const pendingLead = JSON.parse(window.sessionStorage.getItem(businessLeadStorageKey) || "null");
    const age = Date.now() - Date.parse(pendingLead?.event?.submitted_at || "");
    if (pendingLead && Number.isFinite(age) && age < 30 * 60 * 1000) {
      identifyKlaviyoProfile(pendingLead.profile);
      pushKlaviyoEvent("Business Inquiry Confirmed", pendingLead.event);
      pushThemeEvent("business_inquiry_success", pendingLead.analytics);
    }
    window.sessionStorage.removeItem(businessLeadStorageKey);
  } catch {
    window.sessionStorage.removeItem(businessLeadStorageKey);
  }
}

document.addEventListener("submit", async (event) => {
  const form = event.target;
  if (form.matches(".guide-popup-form")) pushThemeEvent("guide_signup_submit");
  if (form.matches(".business-form")) {
    const businessLead = buildBusinessLeadContext(form, true);
    try {
      window.sessionStorage.setItem(businessLeadStorageKey, JSON.stringify(businessLead));
    } catch {}
    identifyKlaviyoProfile(businessLead.profile);
    pushKlaviyoEvent("Business Inquiry Submitted", businessLead.event);
    pushThemeEvent("business_inquiry_submit", businessLead.analytics);
  }
  if (event.submitter?.name === "checkout") pushThemeEvent("cart_checkout_submit");
  if (!form.action?.includes("/cart/add")) return;

  const productCard = form.closest(".catalog-card, .product-info, .sticky-product-bar");
  const title = productCard?.querySelector("h1, h2, h3, span")?.textContent?.trim() || "";
  const productPage = form.closest("[data-product-page]");
  const selectedVariant = form.querySelector("[data-product-variant-select]");
  const selectedOption = selectedVariant?.tagName === "SELECT" ? selectedVariant.selectedOptions[0] : null;
  const quantity = Number(form.querySelector("[name='quantity']")?.value || 1);
  const selectedSellingPlan = form.querySelector("[name='selling_plan']:checked, [name='selling_plan']")?.value || "";
  pushThemeEvent("vivo_add_to_cart_submit", {
    product_title: title,
    product_id: productPage?.dataset.productId || "",
    product_handle: productPage?.dataset.productHandle || "",
    variant_id: selectedVariant?.value || "",
    variant_title: selectedOption?.dataset.variantTitle || "",
    sku: selectedOption?.dataset.sku || "",
    price_cents: Number(selectedOption?.dataset.priceCents || 0),
    quantity,
    has_selling_plan: Boolean(selectedSellingPlan),
    selling_plan_id: selectedSellingPlan
  });

  event.preventDefault();
  if (form.dataset.cartSubmitting === "true") return;
  form.dataset.cartSubmitting = "true";
  const submitter = event.submitter || form.querySelector("button[type='submit']");
  const originalLabel = submitter?.textContent || "Add to cart";
  if (submitter) {
    submitter.disabled = true;
    submitter.textContent = "Adding...";
  }

  try {
    const response = await fetch(cartRoute("cart/add.js"), {
      method: "POST",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
      body: new FormData(form)
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || "This item could not be added.");
    const cart = await fetchCart();
    renderCart(cart);
    pushThemeEvent("vivo_add_to_cart_success", {
      product_title: title,
      product_id: productPage?.dataset.productId || "",
      product_handle: productPage?.dataset.productHandle || "",
      variant_id: selectedVariant?.value || "",
      variant_title: selectedOption?.dataset.variantTitle || "",
      sku: selectedOption?.dataset.sku || "",
      price_cents: Number(selectedOption?.dataset.priceCents || 0),
      quantity,
      has_selling_plan: Boolean(selectedSellingPlan),
      selling_plan_id: selectedSellingPlan,
      item_count: cart.item_count
    });
    await openCartDrawer("add_to_cart", cart);
  } catch (error) {
    await openCartDrawer("add_to_cart_error");
    showCartError(error.message || "This item could not be added.");
  } finally {
    form.dataset.cartSubmitting = "false";
    if (submitter) {
      submitter.disabled = false;
      submitter.textContent = originalLabel;
    }
  }
});

const businessInquirySelect = document.querySelector("[data-business-inquiry-select]");
const businessWholesaleField = document.querySelector("[data-business-wholesale-field]");
const businessPartnershipField = document.querySelector("[data-business-partnership-field]");
const businessMessageLabel = document.querySelector("[data-business-message-label]");
const businessMessage = document.querySelector("[data-business-message]");

const updateBusinessApplication = () => {
  if (!businessInquirySelect) return;
  const inquiryType = businessInquirySelect.value;
  const isWholesale = inquiryType === "Wholesale / retail";
  const isPartnership = [
    "Creator / affiliate partnership",
    "Professional / event partnership",
    "Brand collaboration"
  ].includes(inquiryType);

  if (businessWholesaleField) {
    businessWholesaleField.hidden = !isWholesale;
    const wholesaleSelect = businessWholesaleField.querySelector("select");
    if (wholesaleSelect) wholesaleSelect.disabled = !isWholesale;
  }
  if (businessPartnershipField) {
    businessPartnershipField.hidden = !isPartnership;
    const partnershipSelect = businessPartnershipField.querySelector("select");
    if (partnershipSelect) partnershipSelect.disabled = !isPartnership;
  }

  if (businessMessageLabel && businessMessage) {
    if (isWholesale) {
      businessMessageLabel.textContent = "Tell us about your store and what you want to carry";
      businessMessage.placeholder = "Store type, sales channels, relevant brands, and what you want from a wholesale relationship.";
    } else if (isPartnership) {
      businessMessageLabel.textContent = "Tell us about your audience and proposed partnership";
      businessMessage.placeholder = "Your audience, content or activation idea, deliverables, and why Vivo Nostra is a fit.";
    } else {
      businessMessageLabel.textContent = "Tell us about the opportunity";
      businessMessage.placeholder = "What do you want to build, why is Vivo Nostra a fit, and what would success look like?";
    }
  }
};

businessInquirySelect?.addEventListener("change", () => {
  updateBusinessApplication();
  pushThemeEvent("business_inquiry_type_selected", { inquiry_type: businessInquirySelect.value });
});

document.querySelectorAll("[data-business-path]").forEach((link) => {
  link.addEventListener("click", () => {
    if (!businessInquirySelect) return;
    businessInquirySelect.value = link.dataset.businessPath || "";
    updateBusinessApplication();
    pushThemeEvent("business_path_click", { inquiry_type: businessInquirySelect.value });
  });
});

updateBusinessApplication();

const affiliatePartnerSection = document.querySelector("[data-affiliate-partner]");

if (affiliatePartnerSection) {
  const affiliatePartner = affiliatePartnerSection.dataset.affiliatePartnerId || "";
  const affiliateDiscountCode = affiliatePartnerSection.dataset.affiliateDiscountCode || "";
  const affiliateParams = new URLSearchParams(window.location.search);
  const affiliateLandingKey = `vivo_affiliate_landing_${affiliatePartner}_${window.location.pathname}`;

  if (!window.sessionStorage.getItem(affiliateLandingKey)) {
    pushThemeEvent("affiliate_referral_landing", {
      affiliate_partner: affiliatePartner,
      discount_code: affiliateDiscountCode,
      referral_partner: affiliateParams.get("ref") || "",
      utm_source: affiliateParams.get("utm_source") || "",
      utm_medium: affiliateParams.get("utm_medium") || "",
      utm_campaign: affiliateParams.get("utm_campaign") || ""
    });
    window.sessionStorage.setItem(affiliateLandingKey, "1");
  }

  affiliatePartnerSection.querySelector("[data-affiliate-code-copy]")?.addEventListener("click", async (event) => {
    const button = event.currentTarget;
    const codeOutput = affiliatePartnerSection.querySelector("[data-affiliate-code-output]");
    const originalLabel = button.textContent;
    let copied = false;

    try {
      await navigator.clipboard.writeText(affiliateDiscountCode);
      copied = true;
    } catch {
      if (codeOutput) {
        codeOutput.focus();
        codeOutput.select();
        copied = document.execCommand("copy");
      }
    }

    button.textContent = copied ? "Code copied" : "Select code";
    pushThemeEvent("affiliate_code_copy", {
      affiliate_partner: affiliatePartner,
      discount_code: affiliateDiscountCode,
      copied
    });
    window.setTimeout(() => { button.textContent = originalLabel; }, 1800);
  });
}

document.addEventListener("click", (event) => {
  const clicked = event.target.closest("a, button, summary, input, label");
  const link = event.target.closest("a[href]");
  const href = link?.getAttribute("href") || "";
  const text = clicked?.textContent?.trim().slice(0, 80) || "";

  if (href.includes("best-sellers") || href.includes("sort_by=best-selling")) {
    pushThemeEvent("best_sellers_click", { link_text: text, link_url: link.href });
  }
  if (href.includes("/products/starter-routine")) {
    pushThemeEvent("starter_kit_cta_click", { link_text: text, link_url: link.href });
  }
  if (href.startsWith("mailto:support@vivonostra.us")) {
    pushThemeEvent("support_click", { link_text: text, link_url: href });
  }
  if (clicked?.matches?.("[data-guide-popup-open]")) {
    pushThemeEvent("guide_popup_cta_click", { label: text });
  }
  if (clicked?.matches?.("[data-cart-checkout]")) {
    pushThemeEvent("cart_checkout_submit", { source: "native_drawer" });
  }
  if (link && affiliatePartnerSection?.contains(link)) {
    pushThemeEvent("affiliate_partner_click", {
      affiliate_partner: affiliatePartnerSection.dataset.affiliatePartnerId || "",
      discount_code: affiliatePartnerSection.dataset.affiliateDiscountCode || "",
      link_text: text,
      link_url: link.href
    });
  }
  if (clicked?.matches?.("[name='selling_plan'], [data-selling-plan], [data-appstle-subscription], .appstle_subscription_wrapper *")) {
    const productPage = clicked.closest("[data-product-page]");
    const variantControl = productPage?.querySelector("[data-product-variant-select]");
    pushThemeEvent("subscribe_option_selected", {
      product_id: productPage?.dataset.productId || "",
      product_handle: productPage?.dataset.productHandle || "",
      variant_id: variantControl?.value || "",
      label: text,
      value: clicked.value || clicked.getAttribute("data-selling-plan") || ""
    });
  }
  if (!link?.matches("a[href*='/products/'], a[href*='/collections/']")) return;
  pushThemeEvent("vivo_shop_link_click", {
    link_text: link.textContent.trim().slice(0, 80),
    link_url: link.href
  });
}, { passive: true });
